import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcceptInviteResult, AssistantConnection, AssistantMessage, AssistantSettings, AssistantTask, ExtractTaskInput, ImapConnectionInput, ReplyInput, SendMailInput } from '@accounting/shared';
import { api } from '../lib/api';
import { assistantKeys } from './useAssistant';

/** Phase 3: the attention feed. */
export const mailKeys = {
  settings: () => ['me', 'assistant', 'settings'] as const,
  feed: (state: string, connectionIds: string[]) => ['me', 'assistant', 'messages', state, [...connectionIds].sort().join(',')] as const,
  message: (id: string) => ['me', 'assistant', 'message', id] as const,
};

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: assistantKeys.all });
}

export function useFeed(state: 'open' | 'snoozed' | 'done' | 'inbox', connectionIds: string[] = []) {
  return useQuery({
    queryKey: mailKeys.feed(state, connectionIds),
    queryFn: () =>
      api
        .get<{ messages: AssistantMessage[] }>('/assistant/messages', {
          state,
          limit: '100',
          ...(connectionIds.length > 0 ? { connectionIds: connectionIds.join(',') } : {}),
        })
        .then((r) => r.messages),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Detail (with body). The API fetches the body from the provider the first time. */
export function useMessage(id: string | null) {
  return useQuery({
    queryKey: mailKeys.message(id ?? ''),
    queryFn: () => api.get<{ message: AssistantMessage }>(`/assistant/messages/${id}`).then((r) => r.message),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

function messageMutation<TVars extends { id: string }>(path: (v: TVars) => string, body?: (v: TVars) => unknown) {
  return function useIt() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (v: TVars) => api.post<{ message: AssistantMessage }>(path(v), body ? body(v) : {}).then((r) => r.message),
      onSuccess: () => invalidateAll(qc),
    });
  };
}

export const useReply = messageMutation<ReplyInput & { id: string }>(
  (v) => `/assistant/messages/${v.id}/reply`,
  (v) => ({ bodyText: v.bodyText, replyAll: v.replyAll }),
);
export const useSetFlag = messageMutation<{ id: string; flagged: boolean }>(
  (v) => `/assistant/messages/${v.id}/flag`,
  (v) => ({ flagged: v.flagged }),
);
export const useSetRead = messageMutation<{ id: string; read: boolean }>(
  (v) => `/assistant/messages/${v.id}/read`,
  (v) => ({ read: v.read }),
);
export const useSnooze = messageMutation<{ id: string; until: string }>(
  (v) => `/assistant/messages/${v.id}/snooze`,
  (v) => ({ until: v.until }),
);
export const useMarkDone = messageMutation<{ id: string }>((v) => `/assistant/messages/${v.id}/done`);
export function useJunk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: 'sender' | 'domain' }) =>
      api.post<{ message: AssistantMessage; muted: string | null; movedAtProvider: boolean }>(`/assistant/messages/${id}/junk`, { scope }),
    onSuccess: () => invalidateAll(qc),
  });
}
export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ result: AcceptInviteResult }>(`/assistant/messages/${id}/accept-invite`, {}).then((r) => r.result),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useMute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: 'sender' | 'domain' }) =>
      api.post<{ message: AssistantMessage; muted: string | null }>(`/assistant/messages/${id}/mute`, { scope }),
    onSuccess: () => invalidateAll(qc),
  });
}
export const useReopen = messageMutation<{ id: string }>((v) => `/assistant/messages/${v.id}/reopen`);

export function useExtractTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ExtractTaskInput & { id: string }) =>
      api.post<{ task: AssistantTask }>(`/assistant/messages/${id}/extract-task`, input).then((r) => r.task),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAssistantSettings() {
  return useQuery({
    queryKey: mailKeys.settings(),
    queryFn: () => api.get<{ settings: AssistantSettings }>('/assistant/settings').then((r) => r.settings),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAssistantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<AssistantSettings>) => api.put<{ settings: AssistantSettings }>('/assistant/settings', input).then((r) => r.settings),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSendMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMailInput) => api.post<{ ok: true }>('/assistant/mail/send', input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useConnectImap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImapConnectionInput) =>
      api.post<{ connection: AssistantConnection; isNew: boolean }>('/assistant/connections/imap', input),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Snooze presets, computed in the browser's zone. */
export function snoozePresets(now: Date = new Date()): { label: string; until: Date }[] {
  const at = (d: Date, h: number) => {
    const x = new Date(d);
    x.setHours(h, 0, 0, 0);
    return x;
  };
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const nextMonday = new Date(now);
  nextMonday.setDate(nextMonday.getDate() + ((8 - nextMonday.getDay()) % 7 || 7));
  return [
    { label: 'Later today (+4h)', until: new Date(now.getTime() + 4 * 3_600_000) },
    { label: 'Tomorrow 8am', until: at(tomorrow, 8) },
    { label: 'Next Monday 8am', until: at(nextMonday, 8) },
  ];
}

export const ATTENTION_LABELS: Record<string, string> = {
  needs_reply: 'Needs reply',
  awaiting_reply: 'Awaiting reply',
  flagged: 'Flagged',
  deadline: 'Deadline',
  vip: 'VIP',
  none: '',
};
